import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { ReplyToInvitationDto } from '@/types/dtos/invitations/request/reply-to-invitation.dto.ts';
import { GET_INVITATION_QUERY_KEY } from '../../queries/invitations/use-get-invitation.ts';
import type { GetInvitationDto } from '@/types/dtos/invitations/response/get-invitation.dto.ts';

export const useReplyToInvitationMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ invitationKey, data }: { invitationKey: string; data: ReplyToInvitationDto }) => {
      const token = await getToken();
      return replyToInvitation(invitationKey, data, token ?? '');
    },
    onMutate: async ({ invitationKey }: { invitationKey: string; data: ReplyToInvitationDto }) => {
      await queryClient.cancelQueries({ queryKey: [GET_INVITATION_QUERY_KEY, invitationKey] });

      queryClient.setQueryData([GET_INVITATION_QUERY_KEY, invitationKey], (old: GetInvitationDto | undefined) =>
        old
          ? {
              ...old,
              responseAt: new Date().toISOString(),
            }
          : old,
      );
    },
    onSettled: (_data, _err, { invitationKey }) => {
      void queryClient.invalidateQueries({ queryKey: [GET_INVITATION_QUERY_KEY, invitationKey] });
    },
  });
};

const replyToInvitation = async (invitationKey: string, data: ReplyToInvitationDto, token: string) => {
  const url = `${import.meta.env.VITE_API_URL}/invitations/${invitationKey}/reply`;
  await sendJSONRequest({
    url,
    method: METHOD.POST,
    body: data,
    token,
  });
};
